const stateRank = {
  clear: 0,
  na: 0,
  review: 1,
  block: 2,
};

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const formatPercentage = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : value;

const issueMatchesIngredient = (issue, ingredient) => {
  const haystack = normalize(
    [issue.message, issue.code, issue.source, issue.sourceQuote].filter(Boolean).join(" ")
  );
  const names = [ingredient.name, ingredient.casNumber].map(normalize).filter(Boolean);
  return names.some((name) => name && name !== "n/a" && haystack.includes(name));
};

const issueState = (issue) => (issue.blocking ? "block" : "review");

const maxState = (...states) =>
  states.reduce((winner, state) => (stateRank[state] > stateRank[winner] ? state : winner), "clear");

const makeCell = ({ key, label, state, detail = "", issues = [] }) => ({
  key,
  label,
  state,
  detail,
  issues,
});

const relevantIssues = (report, ingredient) =>
  (report.issues || []).filter((issue) => issueMatchesIngredient(issue, ingredient));

export const buildFormulaRiskMatrix = (report) => {
  const ingredients = report?.ingredients || [];
  const issues = report?.issues || [];

  return ingredients.map((ingredient) => {
    const ingredientIssues = relevantIssues(report, ingredient);
    const ifraIssues = ingredientIssues.filter((issue) => issue.jurisdiction === "IFRA");
    const annexIssues = ingredientIssues.filter((issue) => String(issue.code || "").startsWith("EU_ANNEX"));
    const allergenIssues = ingredientIssues.filter((issue) => issue.code === "EU_LABEL_ALLERGEN_REVIEW");
    const identityIssues = ingredientIssues.filter((issue) =>
      ["IFRA_INGREDIENT_UNVERIFIED", "EU_INGREDIENT_IDENTITY_REVIEW", "EU_COSING_IDENTITY_MISSING"].includes(issue.code)
    );
    const globalUnitIssues = issues.filter((issue) =>
      ["EU_CONCENTRATION_UNVERIFIED", "US_CONCENTRATION_UNVERIFIED"].includes(issue.code)
    );
    const unitIssues = ingredientIssues.filter((issue) =>
      String(issue.code || "").includes("CONCENTRATION_UNVERIFIED")
    );

    const unitState = ingredient.concentrationVerified ? "clear" : "block";
    const identityState = !ingredient.validIngredient
      ? "block"
      : identityIssues.length
        ? maxState(...identityIssues.map(issueState))
        : "clear";
    const ifraState = ifraIssues.length
      ? maxState(...ifraIssues.map(issueState))
      : ingredient.ifraAboveThreshold
        ? "block"
        : "clear";
    const annexState = annexIssues.length
      ? maxState(...annexIssues.map(issueState))
      : "clear";
    const allergenState = allergenIssues.length ? "review" : "clear";
    const overallState = maxState(unitState, identityState, ifraState, annexState, allergenState);

    const cells = [
      makeCell({
        key: "unit",
        label: "Unit",
        state: unitState,
        detail: ingredient.concentrationVerified
          ? `${formatPercentage(ingredient.formulaPercentage)}%${ingredient.concentrationUnit ? ` from ${ingredient.concentrationUnit}` : ""}`
          : ingredient.concentrationIssue || "Concentration unit not verified.",
        issues: [...unitIssues, ...(!ingredient.concentrationVerified ? globalUnitIssues : [])],
      }),
      makeCell({
        key: "identity",
        label: "Identity",
        state: identityState,
        detail: ingredient.validIngredient ? ingredient.matchType || "Matched" : "Ingredient identity not verified.",
        issues: identityIssues,
      }),
      makeCell({
        key: "ifra",
        label: "IFRA",
        state: ifraState,
        detail: ingredient.ifraAboveThreshold
          ? `Above Category 4 threshold ${ingredient.ifraCategory4Threshold}%`
          : ingredient.ifraCategory4Threshold && ingredient.ifraCategory4Threshold !== "N/A"
            ? `Category 4 threshold ${ingredient.ifraCategory4Threshold}%`
            : "No Category 4 threshold hit.",
        issues: ifraIssues,
      }),
      makeCell({
        key: "euAnnex",
        label: "EU Annex",
        state: annexState,
        detail: annexIssues.length ? annexIssues.map((issue) => issue.code).join(", ") : "No Annex II/III hit.",
        issues: annexIssues,
      }),
      makeCell({
        key: "allergen",
        label: "Allergen",
        state: allergenState,
        detail: allergenIssues.length ? "EU label declaration review." : "No allergen review issue.",
        issues: allergenIssues,
      }),
    ];

    return {
      id: `${ingredient.name || "ingredient"}-${ingredient.casNumber || "cas"}`,
      ingredient,
      cells,
      overall: makeCell({
        key: "overall",
        label: "Overall",
        state: overallState,
        detail:
          overallState === "block"
            ? "Blocking review required."
            : overallState === "review"
              ? "Non-blocking review required."
              : "No screening issue found.",
        issues: ingredientIssues,
      }),
      issues: ingredientIssues,
    };
  });
};

export const summarizeFormulaRiskMatrix = (rows = []) => ({
  ingredients: rows.length,
  blocked: rows.filter((row) => row.overall.state === "block").length,
  review: rows.filter((row) => row.overall.state === "review").length,
  clear: rows.filter((row) => row.overall.state === "clear").length,
});
